/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  BrickLoader,
  Pagination,
  UniversalWrap,
  CostTableData,
  StackBarChart,
  Wrapper,
  getCostAnalysisTableColumns,
  tooltipStyles,
  generateLinkParameters,
  mapStackBarChartColors,
  ChartType,
  CostAnalysisFields,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { useApplicationsCostPerApplication } from "../hooks";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  showCostTable: boolean;
  showOtherApplicationsCost: boolean;
  handleOtherCostsSwitch: (enable: boolean) => void;
};

export function ApplicationsCostPerApplicationView({
  startDate,
  endDate,
  granularity,
  period,
  showCostTable,
  showOtherApplicationsCost,
  handleOtherCostsSwitch,
}: Props) {
  const { currency } = useCurrency();

  const {
    costPerApplicationChartData,
    costPerApplicationTableData,
    costPerApplicationTotal,
    loading,
    error,
    count,
    applications,
    isCorrections,
  } = useApplicationsCostPerApplication({
    startDate,
    endDate,
    granularity,
    showOtherApplicationsCost,
    handleOtherCostsSwitch,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const stackBarColors = useMemo(
    () => mapStackBarChartColors(applications),
    [applications]
  );

  const allColumns = useMemo(
    () =>
      getCostAnalysisTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerApplicationTableData,
        period,
        chartType: ChartType.COST_PER_APPLICATION,
        colors: stackBarColors,
        linkParameters: generateLinkParameters(
          LinkQueryParameterName.APPLICATION,
          []
        ),
      }),
    [
      startDate,
      endDate,
      currency,
      costPerApplicationTableData,
      period,
      showOtherApplicationsCost,
      stackBarColors,
    ]
  );

  // hide total column when period is month
  const columns = useMemo(
    () =>
      period === Period.MONTH
        ? allColumns.filter((column) => column.header !== "Total")
        : allColumns,
    [allColumns, period]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <Wrapper onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <StackBarChart
                width={parent.width}
                height={280}
                data={costPerApplicationChartData}
                granularity={granularity}
                loading={loading}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                showOthers={showOtherApplicationsCost}
                colors={stackBarColors}
                chartType={ChartType.COST_PER_APPLICATION}
                containerRef={containerRef}
                keys={
                  isCorrections
                    ? [...applications, CostAnalysisFields.CORRECTION_COST]
                    : applications
                }
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set this to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </Wrapper>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerApplicationTableData}
        chartType={ChartType.COST_PER_APPLICATION}
        total={costPerApplicationTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable && isNotEmpty(costPerApplicationChartData)}
        hasFooter={false}
        hideHeader={period === Period.MONTH}
      />
      <Spacer height={theme.spacing.spacing03} />
      <Pagination small count={count ?? 0} />
    </UniversalWrap>
  );
}
